// src/components/dashboard/InventoryIntelligence.jsx

import React, { useMemo } from 'react';
import { COLORS } from '../../constants';
import { DashboardSection, PAGE } from '../ui';
import {
  findHeader,
  shortText,
  hasMeaningfulValue,
  getStatusColor,
} from '../../utils/helpers';
import Donut from './Donut';

export default function InventoryIntelligence({ data }) {
  const headers = useMemo(
    () => (data?.length ? Object.keys(data[0]).filter((h) => h.trim()) : []),
    [data]
  );

  const columnMap = useMemo(
    () => ({
      productGroup: findHeader(headers, [
        /^description$/i,
        /^name$/i,
        /equipment\s*name/i,
        /device\s*name/i,
        /asset\s*name/i,
        /product\s*name/i,
        /item\s*name/i,
        /description/i,
        /name/i,
        /product\s*id/i,
        /product\s*code/i,
        /^pid$/i,
      ]),

      scanHeaders: headers.filter((header) => {
        const h = String(header || '').toLowerCase();

        return (
          /scan/i.test(h) &&
          !/condition/i.test(h) &&
          !/\bcond\b/i.test(h) &&
          !/image/i.test(h) &&
          !/path/i.test(h)
        );
      }),

      conditionHeaders: headers.filter((header) => {
        const h = String(header || '').toLowerCase();

        return (
          (
            /condition/i.test(h) ||
            /\bcond\b/i.test(h) ||
            /status/i.test(h)
          ) &&
          !/scan/i.test(h) &&
          !/image/i.test(h) &&
          !/path/i.test(h)
        );
      }),
    }),
    [headers]
  );

  const analytics = useMemo(() => {
    const emptyAnalytics = {
      totalListed: 0,
      scanned: 0,
      confirmedGood: 0,
      confirmedDamaged: 0,
      pendingInspection: 0,
      scanCompletion: 0,
      confirmedGoodRate: 0,
      productGroups: 0,
      products: [],
    };

    if (!data?.length) {
      return emptyAnalytics;
    }

    const normalize = (value) => String(value || '').trim().toLowerCase();

    const isEmptyLikeValue = (value) => {
      const text = normalize(value);

      return (
        !text ||
        text === '-' ||
        text === '—' ||
        text === 'na' ||
        text === 'n/a' ||
        text === 'null' ||
        text === 'undefined'
      );
    };

    const isNegativeScanValue = (value) => {
      const text = normalize(value);

      return [
        'no',
        'n',
        'false',
        'not scanned',
        'pending',
        'not verified',
        'unverified',
        'incomplete',
        'missing',
        'fail',
        'failed',
        '-',
        '—',
      ].includes(text);
    };

    const hasRealScan = (row) => {
      const scanHeaders = columnMap.scanHeaders || [];

      if (!scanHeaders.length) {
        return false;
      }

      return scanHeaders.some((header) => {
        const value = row[header];

        if (!hasMeaningfulValue(value)) return false;
        if (isEmptyLikeValue(value)) return false;
        if (isNegativeScanValue(value)) return false;

        return true;
      });
    };

    const classifyConditionValue = (value) => {
      const text = normalize(value);
      const statusColor = getStatusColor(value);

      if (isEmptyLikeValue(value)) {
        return 'pending';
      }

      const goodPhrases = [
        'no damage',
        'not damaged',
        'undamaged',
        'no issue',
        'no issues',
        'good',
        'ok',
        'pass',
        'passed',
        'working',
        'available',
        'healthy',
        'normal',
      ];

      const damagedPhrases = [
        'damaged',
        'damage found',
        'has damage',
        'damage',
        'bad',
        'fail',
        'failed',
        'not ok',
        'not working',
        'broken',
        'faulty',
        'defective',
      ];

      // Important: check good phrases first because "No Damage" contains "damage".
      if (goodPhrases.some((phrase) => text.includes(phrase))) {
        return 'good';
      }

      if (
        statusColor === 'danger' ||
        damagedPhrases.some((phrase) => text.includes(phrase))
      ) {
        return 'damaged';
      }

      if (statusColor === 'success') {
        return 'good';
      }

      return 'pending';
    };

    const getConditionCategory = (row) => {
      const conditionHeaders = columnMap.conditionHeaders || [];

      if (!conditionHeaders.length) {
        return 'pending';
      }

      const classifications = conditionHeaders.map((header) =>
        classifyConditionValue(row[header])
      );

      if (classifications.includes('damaged')) {
        return 'damaged';
      }

      if (classifications.includes('good')) {
        return 'good';
      }

      return 'pending';
    };

    const productMap = new Map();

    let scanned = 0;
    let confirmedGood = 0;
    let confirmedDamaged = 0;

    data.forEach((row) => {
      const rawProductGroup = row[columnMap.productGroup];
      const productGroup = hasMeaningfulValue(rawProductGroup)
        ? String(rawProductGroup).trim()
        : 'Unknown Product';

      const isScanned = hasRealScan(row);
      const conditionCategory = getConditionCategory(row);

      const isConfirmedGood = isScanned && conditionCategory === 'good';
      const isConfirmedDamaged = isScanned && conditionCategory === 'damaged';

      if (isScanned) scanned += 1;
      if (isConfirmedGood) confirmedGood += 1;
      if (isConfirmedDamaged) confirmedDamaged += 1;

      if (!productMap.has(productGroup)) {
        productMap.set(productGroup, {
          productGroup,
          listed: 0,
          scanned: 0,
          confirmedGood: 0,
          confirmedDamaged: 0,
          pendingInspection: 0,
        });
      }

      const product = productMap.get(productGroup);

      product.listed += 1;

      if (isScanned) {
        product.scanned += 1;
      }

      if (isConfirmedGood) {
        product.confirmedGood += 1;
      } else if (isConfirmedDamaged) {
        product.confirmedDamaged += 1;
      } else {
        product.pendingInspection += 1;
      }
    });

    const totalListed = data.length;
    const pendingInspection = totalListed - confirmedGood - confirmedDamaged;

    return {
      totalListed,
      scanned,
      confirmedGood,
      confirmedDamaged,
      pendingInspection,
      scanCompletion:
        totalListed > 0 ? Math.round((scanned / totalListed) * 100) : 0,
      confirmedGoodRate:
        totalListed > 0
          ? Math.round((confirmedGood / totalListed) * 100)
          : 0,
      productGroups: productMap.size,
      products: Array.from(productMap.values())
        .sort((a, b) => {
          if (b.listed !== a.listed) return b.listed - a.listed;
          return b.confirmedDamaged - a.confirmedDamaged;
        })
        .slice(0, 6),
    };
  }, [data, columnMap]);

  const getRateColor = (rate) => {
    if (rate >= 80) return COLORS.success;
    if (rate >= 50) return COLORS.warning;
    return COLORS.danger;
  };

  const getProductStatus = (product) => {
    if (
      product.scanned === 0 &&
      product.confirmedGood === 0 &&
      product.confirmedDamaged === 0
    ) {
      return {
        label: 'Not Started',
        color: COLORS.warning,
        background: COLORS.warningLight,
      };
    }

    if (product.confirmedDamaged > 0) {
      return {
        label: 'Needs Review',
        color: COLORS.danger,
        background: COLORS.dangerLight,
      };
    }

    if (
      product.scanned === product.listed &&
      product.pendingInspection === 0 &&
      product.confirmedDamaged === 0
    ) {
      return {
        label: 'Completed',
        color: COLORS.success,
        background: COLORS.successLight,
      };
    }

    return {
      label: 'In Progress',
      color: COLORS.info,
      background: '#eaf4fd',
    };
  };

  return (
    <DashboardSection
      title="Inventory Intelligence"
      subtitle="Scan completion, confirmed condition status & product inspection summary"
      style={{ height: '100%' }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div
          style={{
            padding: '10px 12px',
            borderRadius: 12,
            background: '#f8fafc',
            border: `1px solid ${PAGE.colors.line}`,
            fontSize: 12,
            color: PAGE.colors.muted,
            lineHeight: 1.5,
            fontWeight: 600,
          }}
        >
          Listed assets are counted from the inventory. Confirmed Good and
          Confirmed Damaged are counted only after an asset has a valid scan
          value. Unscanned or unconfirmed assets remain Pending Inspection.
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
            gap: 12,
          }}
        >
          <div
            style={{
              background: COLORS.bg,
              borderRadius: 14,
              padding: 14,
              border: `1px solid ${PAGE.colors.line}`,
            }}
          >
            <div
              style={{
                fontSize: 11,
                color: PAGE.colors.muted,
                fontWeight: 700,
              }}
            >
              Scan Completion
            </div>

            <div
              style={{
                marginTop: 6,
                fontSize: 22,
                fontWeight: 900,
                color: getRateColor(analytics.scanCompletion),
              }}
            >
              {analytics.scanCompletion}%
            </div>

            <div style={{ fontSize: 11, color: PAGE.colors.muted }}>
              {analytics.scanned}/{analytics.totalListed} assets scanned
            </div>
          </div>

          <div
            style={{
              background: COLORS.bg,
              borderRadius: 14,
              padding: 14,
              border: `1px solid ${PAGE.colors.line}`,
            }}
          >
            <div
              style={{
                fontSize: 11,
                color: PAGE.colors.muted,
                fontWeight: 700,
              }}
            >
              Confirmed Good
            </div>

            <div
              style={{
                marginTop: 6,
                fontSize: 22,
                fontWeight: 900,
                color: getRateColor(analytics.confirmedGoodRate),
              }}
            >
              {analytics.confirmedGoodRate}%
            </div>

            <div style={{ fontSize: 11, color: PAGE.colors.muted }}>
              {analytics.confirmedGood}/{analytics.totalListed} assets
              confirmed good
            </div>
          </div>

          <div
            style={{
              background: COLORS.bg,
              borderRadius: 14,
              padding: 14,
              border: `1px solid ${PAGE.colors.line}`,
            }}
          >
            <div
              style={{
                fontSize: 11,
                color: PAGE.colors.muted,
                fontWeight: 700,
              }}
            >
              Product Groups
            </div>

            <div
              style={{
                marginTop: 6,
                fontSize: 22,
                fontWeight: 900,
                color: PAGE.colors.heading,
              }}
            >
              {analytics.productGroups}
            </div>

            <div style={{ fontSize: 11, color: PAGE.colors.muted }}>
              grouped by asset/product description
            </div>
          </div>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1.2fr',
            gap: 20,
          }}
        >
          <div style={{ background: COLORS.bg, borderRadius: 16, padding: 20 }}>
            <h3 style={PAGE.type.subsectionTitle}>Inspection Status</h3>

            <div
              style={{
                display: 'flex',
                justifyContent: 'space-around',
                marginBottom: 16,
              }}
            >
              <Donut
                value={analytics.confirmedGood}
                total={analytics.totalListed}
                size={80}
                thickness={8}
                color={COLORS.success}
                label="Confirmed Good"
              />

              <Donut
                value={analytics.confirmedDamaged}
                total={analytics.totalListed}
                size={80}
                thickness={8}
                color={COLORS.danger}
                label="Confirmed Damaged"
              />

              <Donut
                value={analytics.pendingInspection}
                total={analytics.totalListed}
                size={80}
                thickness={8}
                color={COLORS.warning}
                label="Pending Inspection"
              />
            </div>

            <div
              style={{
                height: 6,
                background: COLORS.line,
                borderRadius: 10,
                display: 'flex',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  width: `${
                    analytics.totalListed > 0
                      ? (analytics.confirmedGood / analytics.totalListed) * 100
                      : 0
                  }%`,
                  background: COLORS.success,
                }}
              />

              <div
                style={{
                  width: `${
                    analytics.totalListed > 0
                      ? (analytics.confirmedDamaged /
                          analytics.totalListed) *
                        100
                      : 0
                  }%`,
                  background: COLORS.danger,
                }}
              />

              <div
                style={{
                  width: `${
                    analytics.totalListed > 0
                      ? (analytics.pendingInspection /
                          analytics.totalListed) *
                        100
                      : 0
                  }%`,
                  background: COLORS.warning,
                }}
              />
            </div>
          </div>

          <div style={{ background: COLORS.bg, borderRadius: 16, padding: 20 }}>
            <h3 style={PAGE.type.subsectionTitle}>
              Product Inspection Summary
            </h3>

            {analytics.products.length === 0 ? (
              <div style={{ fontSize: 12, color: PAGE.colors.muted }}>
                No product inspection data available.
              </div>
            ) : (
              analytics.products.map((product) => {
                const confirmedGoodRate =
                  product.listed > 0
                    ? Math.round(
                        (product.confirmedGood / product.listed) * 100
                      )
                    : 0;

                const scanRate =
                  product.listed > 0
                    ? Math.round((product.scanned / product.listed) * 100)
                    : 0;

                const status = getProductStatus(product);

                return (
                  <div key={product.productGroup} style={{ marginBottom: 14 }}>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        marginBottom: 5,
                        gap: 10,
                      }}
                    >
                      <span style={{ fontSize: 13, fontWeight: 700 }}>
                        {shortText(product.productGroup, 22)}
                      </span>

                      <span
                        style={{
                          fontSize: 10,
                          color: status.color,
                          background: status.background,
                          fontWeight: 900,
                          padding: '3px 8px',
                          borderRadius: 999,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {status.label}
                      </span>
                    </div>

                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        marginBottom: 5,
                        gap: 10,
                      }}
                    >
                      <span style={{ fontSize: 11, color: PAGE.colors.muted }}>
                        {product.scanned}/{product.listed} scanned
                      </span>

                      <span
                        style={{
                          fontSize: 11,
                          color: getRateColor(confirmedGoodRate),
                          fontWeight: 800,
                        }}
                      >
                        {confirmedGoodRate}% confirmed good
                      </span>
                    </div>

                    <div
                      style={{
                        height: 4,
                        background: COLORS.line,
                        borderRadius: 10,
                        overflow: 'hidden',
                      }}
                    >
                      <div
                        style={{
                          width: `${scanRate}%`,
                          height: '100%',
                          background: getRateColor(scanRate),
                          borderRadius: 10,
                        }}
                      />
                    </div>

                    <div
                      style={{
                        fontSize: 10,
                        color: PAGE.colors.muted,
                        marginTop: 4,
                      }}
                    >
                      {product.listed} listed | {product.confirmedGood} good |{' '}
                      {product.confirmedDamaged} damaged |{' '}
                      {product.pendingInspection} pending inspection
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </DashboardSection>
  );
}